import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Store, Eye, Edit, Trash2, Globe, ShoppingBag, 
  Users, DollarSign, BarChart3, Search, Filter, ExternalLink,
  CheckCircle, XCircle, Palette, Settings
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const SHOP_TYPE_ICONS = {
  ecommerce: ShoppingBag,
  portfolio: Palette,
  business: Store,
  service: Settings,
  landing: Globe
};

const SHOP_TYPE_COLORS = {
  ecommerce: 'text-primary',
  portfolio: 'text-green-500',
  business: 'text-blue-500',
  service: 'text-purple-500',
  landing: 'text-orange-500'
};

const ShopBuilder = () => {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [templates, setTemplates] = useState({});

  useEffect(() => {
    fetchShops();
    fetchStats();
    fetchTemplates();
  }, []);

  const fetchShops = async () => {
    try {
      const res = await api.get('/admin/shops/all');
      setShops(res.data.shops || []);
    } catch (error) {
      toast.error('Failed to load shops');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/shops/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/shops/templates');
      setTemplates(res.data.templates || {});
    } catch (error) {
      console.error('Failed to load templates');
    }
  };

  const handleDelete = async (shopId, shopName) => {
    if (!window.confirm(`Are you sure you want to delete "${shopName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/shops/${shopId}`);
      toast.success('Shop deleted');
      fetchShops();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete shop');
    }
  };

  const filteredShops = shops.filter(shop => {
    const matchesSearch = shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shop.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || shop.type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="shop-builder-page">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Shop Builder</h1>
          <p className="text-muted-foreground">Create and manage unlimited websites & webshops</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create New Shop
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card p-4">
            <Store className="w-6 h-6 text-primary mb-2" />
            <p className="text-2xl font-bold">{stats.total_shops}</p>
            <p className="text-sm text-muted-foreground">Total Shops</p>
          </div>
          <div className="glass-card p-4">
            <Globe className="w-6 h-6 text-green-500 mb-2" />
            <p className="text-2xl font-bold">{stats.published_shops}</p>
            <p className="text-sm text-muted-foreground">Published</p>
          </div>
          <div className="glass-card p-4">
            <ShoppingBag className="w-6 h-6 text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{stats.total_orders}</p>
            <p className="text-sm text-muted-foreground">Total Orders</p>
          </div>
          <div className="glass-card p-4">
            <DollarSign className="w-6 h-6 text-accent mb-2" />
            <p className="text-2xl font-bold">₹{stats.total_revenue?.toFixed(0) || 0}</p>
            <p className="text-sm text-muted-foreground">Revenue</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search shops..."
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-3 bg-background border border-white/10 rounded-xl text-foreground"
          style={{ colorScheme: 'dark' }}
        >
          <option value="all" className="bg-background text-foreground">All Types</option>
          <option value="ecommerce" className="bg-background text-foreground">E-Commerce</option>
          <option value="portfolio" className="bg-background text-foreground">Portfolio</option>
          <option value="business" className="bg-background text-foreground">Business</option>
          <option value="service" className="bg-background text-foreground">Service</option>
          <option value="landing" className="bg-background text-foreground">Landing Page</option>
        </select>
      </div>

      {/* Shops Grid */}
      {filteredShops.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Store className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Shops Yet</h3>
          <p className="text-muted-foreground mb-6">Create your first website or webshop</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <Plus className="w-5 h-5 inline mr-2" />
            Create Shop
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredShops.map((shop) => {
            const TypeIcon = SHOP_TYPE_ICONS[shop.type] || Store;
            const typeColor = SHOP_TYPE_COLORS[shop.type] || 'text-primary';
            
            return (
              <div key={shop.id} className="glass-card overflow-hidden group">
                {/* Shop Preview Header */}
                <div 
                  className="h-32 relative"
                  style={{ backgroundColor: shop.colors?.primary || '#D4AF37' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" />
                  {shop.logo && (
                    <img 
                      src={shop.logo} 
                      alt={shop.name}
                      className="absolute bottom-4 left-4 w-12 h-12 rounded-xl object-cover border-2 border-white"
                    />
                  )}
                  <div className="absolute top-4 right-4 flex gap-2">
                    {shop.is_published ? (
                      <span className="px-2 py-1 bg-green-500/20 text-green-500 rounded text-xs flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Published
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded text-xs flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Draft
                      </span>
                    )}
                  </div>
                </div>

                {/* Shop Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-lg">{shop.name}</h3>
                      <p className="text-sm text-muted-foreground">/{shop.slug}</p>
                    </div>
                    <TypeIcon className={`w-5 h-5 ${typeColor}`} />
                  </div>

                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {shop.description || 'No description'}
                  </p>

                  {/* Stats */}
                  <div className="flex gap-4 text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                      {shop.views || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <ShoppingBag className="w-4 h-4 text-muted-foreground" />
                      {shop.products?.length || 0} products
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      ₹{shop.revenue || 0}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/shop-builder/${shop.id}`)}
                      className="flex-1 px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <a
                      href={`/shop/${shop.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleDelete(shop.id, shop.name)}
                      className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Shop Modal */}
      {showCreateModal && (
        <CreateShopModal
          templates={templates}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchShops();
            fetchStats();
          }}
        />
      )}
      </div>
    </div>
  );
};

// Create Shop Modal Component
const CreateShopModal = ({ templates, onClose, onCreated }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    type: 'ecommerce',
    template: 'ecommerce_starter',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSlugChange = (name) => {
    const slug = name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData({ ...formData, name, slug });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/admin/shops/create', formData);
      toast.success('Shop created successfully!');
      onCreated();
      navigate(`/admin/shop-builder/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shop');
    } finally {
      setLoading(false);
    }
  };

  const templateOptions = Object.entries(templates).filter(([key, t]) => 
    formData.type === 'all' || t.type === formData.type
  );

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="glass-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Create New Shop</h2>

        {step === 1 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Step 1: Choose Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { value: 'ecommerce', label: 'E-Commerce', icon: ShoppingBag, desc: 'Online store' },
                { value: 'portfolio', label: 'Portfolio', icon: Palette, desc: 'Showcase work' },
                { value: 'business', label: 'Business', icon: Store, desc: 'Company site' },
                { value: 'service', label: 'Service', icon: Settings, desc: 'Service provider' },
                { value: 'landing', label: 'Landing Page', icon: Globe, desc: 'Single page' }
              ].map(({ value, label, icon: Icon, desc }) => (
                <button
                  key={value}
                  onClick={() => setFormData({ ...formData, type: value, template: `${value}_starter` })}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.type === value
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <Icon className={`w-8 h-8 mb-2 ${formData.type === value ? 'text-primary' : 'text-muted-foreground'}`} />
                  <p className="font-semibold">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setStep(2)}
              className="w-full btn-primary"
            >
              Next: Choose Template
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Step 2: Choose Template</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(templates).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => setFormData({ ...formData, template: key })}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.template === key
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <div 
                    className="h-20 rounded-lg mb-3"
                    style={{ background: `linear-gradient(135deg, ${template.colors.primary}, ${template.colors.secondary})` }}
                  />
                  <p className="font-semibold">{template.name}</p>
                  <p className="text-xs text-muted-foreground">{template.description}</p>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 px-4 py-3 bg-white/5 rounded-full">
                Back
              </button>
              <button onClick={() => setStep(3)} className="flex-1 btn-primary">
                Next: Details
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Step 3: Shop Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Shop Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="My Awesome Shop"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL Slug *</label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">/shop/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="my-awesome-shop"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  rows={3}
                  placeholder="Describe your shop..."
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 px-4 py-3 bg-white/5 rounded-full">
                Back
              </button>
              <button 
                onClick={handleCreate} 
                disabled={loading}
                className="flex-1 btn-primary"
              >
                {loading ? 'Creating...' : 'Create Shop'}
              </button>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default ShopBuilder;
